/**
 * Parses the `<op>:<YYYYMMDD[HHMM]>` entries the DateFilter component emits
 * (and that the S&D list pages round-trip through the URL).
 *
 * Shape alone is not enough to trust a value. These arrive from the URL, so a
 * hand-typed or shared link can carry `20260230` or `202607302460` — all
 * digits, right length, impossible. JS would quietly roll those forward
 * (Feb 30 → Mar 2), and the page would show a different day's rows while
 * looking like it had honoured the filter. So a value is only accepted if the
 * Date built from it reports back the same components: the same construct-then-
 * verify check used elsewhere in the app for AS/400 `YYYYMMDD` fields.
 *
 * Anything impossible is dropped, exactly like a malformed entry.
 */

const DATE_OR_DATETIME = /^(\d{4})(\d{2})(\d{2})(?:(\d{2})(\d{2}))?$/;

export interface DateFilterEntry {
  /** `gte`, `lte`, `eq`, … — the caller decides which it honours. */
  op: string;
  /** The raw `YYYYMMDD[HHMM]` value, as picked. */
  value: string;
  year: number;
  /** 0-based, ready for the `Date` constructor. */
  monthIndex: number;
  day: number;
  /** Undefined when the entry is a bare date. */
  hour?: number;
  minute?: number;
}

export function parseDateFilterEntries(values: string[]): DateFilterEntry[] {
  const entries: DateFilterEntry[] = [];
  for (const raw of values) {
    const entry = parseEntry(raw);
    if (entry !== null) entries.push(entry);
  }
  return entries;
}

/** One `<op>:<value>` entry, or null if it isn't one we can trust. */
function parseEntry(raw: string): DateFilterEntry | null {
  // `<= 0` also rejects a leading colon (`:20260115`), which would otherwise
  // parse as an entry with an empty operator.
  const colonIdx = raw.indexOf(':');
  if (colonIdx <= 0) return null;

  const value = raw.slice(colonIdx + 1);
  const match = DATE_OR_DATETIME.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!isRealCalendarDate(year, monthIndex, day)) return null;

  const time = parseTime(match[4], match[5]);
  if (time === null) return null;

  return {
    op: raw.slice(0, colonIdx),
    value,
    year,
    monthIndex,
    day,
    ...time,
  };
}

/**
 * The optional `HHMM` half. Absent is fine (a bare date); present but not a
 * real clock time is not — `null` rejects the whole entry.
 */
function parseTime(
  rawHour: string | undefined,
  rawMinute: string | undefined,
): { hour?: number; minute?: number } | null {
  if (rawHour === undefined) return { hour: undefined, minute: undefined };

  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (hour > 23 || minute > 59) return null;

  return { hour, minute };
}

/** A date JS won't have to roll forward — Feb 30 and friends are rejected. */
function isRealCalendarDate(
  year: number,
  monthIndex: number,
  day: number,
): boolean {
  const date = new Date(year, monthIndex, day);
  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === year &&
    date.getMonth() === monthIndex &&
    date.getDate() === day
  );
}
